import { ApolloClient } from '@apollo/client';
import { blinkConstants } from '@thoughtspot/blink-constants';
import { BlinkContextOptions } from '@thoughtspot/blink-context';
import {
    addCustomCssElements,
    IncomingEventType,
    setCustomActions,
    setCustomVariablesForThirdPartyToolsFromAppInit,
    setEmbedQueryParamsFromInitData,
    setHiddenHomeLeftNavItems,
    setHiddenHomepageModules,
    setHiddenListColumns,
    setHostConfig,
    setInterceptTimeout,
    setInterceptUrls,
    setReorderedHomepageModules,
    setRuntimeFilterParams,
    setRuntimeParameterParams,
} from '@thoughtspot/embed-util';
import { isThisPeriodInDateFiltersEnabled } from '@thoughtspot/session-service';
import {
    getCustomChartContext,
    mapSDKQueryToDataQueryInput,
} from '@thoughtspot/ts-chart-app';
import _ from 'lodash';
import { FilterGroup } from '/@services/generated/graphql-types';
import {
    dispatchNonMemoEvent,
    subscribe,
} from '/@utils/event-bridge/event-bridge';
import { AnswerPageConfigInterface } from '../services/answer/answer.util';
import { setAuthToken } from './http-request-polyfill';

export * from '@thoughtspot/embed-util';

export const subscribeToCustomCssEvents = () => {
    const clean = subscribe(IncomingEventType.UpdateCustomCss, (data: any) => {
        addCustomCssElements(data);
    });

    // this tells the parent that the dynamic css node reloading is ab
    dispatchNonMemoEvent(blinkConstants.embedEvents.DynamicCssNodeReady, {});

    return clean;
};

// TODO : move the below functions to embed-util package.
export interface EmbedAnswerData {
    answerSessionId: string;
    vizId?: string;
    getAnswerPageConfig: () => AnswerPageConfigInterface;
    useAppContext: () => BlinkContextOptions;
    getCachedAnswerForHasUnsavedChanges: any;
    hasUnsavedChanges: any;
    useGetAnswerColumnsAndDataQuery: any;
    getAnswerHeaderMetadataFromCache: any;
    client: ApolloClient<object>;
}
export const useEmbedAnswerData = ({
    answerSessionId,
    vizId,
    getAnswerPageConfig,
    useAppContext,
    hasUnsavedChanges,
    useGetAnswerColumnsAndDataQuery,
    getAnswerHeaderMetadataFromCache,
    client,
}: EmbedAnswerData) => {
    const { sessionService } = useAppContext();
    const { referenceSavedState } = getAnswerPageConfig();
    const headerMetadata = getAnswerHeaderMetadataFromCache(
        answerSessionId,
        client,
    );
    // Get dataQueryInput from custom chart context for custom charts
    const { chartConfigQueries } = getCustomChartContext();
    const dataQueryInput = mapSDKQueryToDataQueryInput(
        chartConfigQueries || [],
    );

    const columnsAndData = answerSessionId
        ? useGetAnswerColumnsAndDataQuery(
              answerSessionId,
              headerMetadata?.displayMode,
              dataQueryInput,
          )
        : {};
    const { getUserEmail, getUserGuid, getUserName }: any = sessionService;
    const userInfo = {
        userName: getUserName(),
        userGUID: getUserGuid(),
        userEmail: getUserEmail(),
    };
    const reportBookMetadata = {
        id: referenceSavedState?.id,
        description: referenceSavedState?.description,
        metadata: referenceSavedState?.metadata,
        name: referenceSavedState?.name,
        suggestedDisplayMode: referenceSavedState?.suggestedDisplayMode,
        visualizations: referenceSavedState?.visualizations,
        headerMetadata,
    };
    const isAnswerUnsaved = hasUnsavedChanges;
    const vizIdObj = vizId ? { vizId } : {};
    return {
        ...columnsAndData,
        user: userInfo,
        reportBookMetadata,
        isAnswerUnsaved,
        ...vizIdObj,
    };
};

/**
 * Strips includeCurrentPeriod from date filters when feature flag is disabled.
 * This ensures embed don't receive this field unless the feature is enabled.
 */
export const sanitizeFiltersForEmbed = (filterGroups: FilterGroup[]) => {
    if (
        isThisPeriodInDateFiltersEnabled() ||
        !filterGroups ||
        !Array.isArray(filterGroups)
    ) {
        return filterGroups;
    }
    return filterGroups.map(filterGroup => ({
        ...filterGroup,
        filters: filterGroup?.filters?.map(filter => ({
            ...filter,
            dateFilterContent: filter?.dateFilterContent?.map(
                dateFilterContent => {
                    if (
                        dateFilterContent?.dateFilter?.includeCurrentPeriod ===
                        undefined
                    ) {
                        return dateFilterContent;
                    }
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        includeCurrentPeriod: _includeCurrentPeriod,
                        ...rest
                    } = dateFilterContent.dateFilter ?? ({} as any);
                    return { ...dateFilterContent, dateFilter: rest };
                },
            ),
        })),
    }));
};

export const clearEmbedSessionInfoCache = () => {
    dispatchNonMemoEvent(blinkConstants.embedEvents.CLEAR_INFO_CACHE, {});
};

/**
 * This function should only be used to set embed data ,
 * rest setup should be done in setupEmbedInit
 *
 * States used inside should read from embedConfig state and not embed.utils
 *
 * @param data - The data store in embed util file
 *
 */
export const setEmbedInitDataValues = (data: any) => {
    const hiddenHomeLeftNavItems = data?.hiddenHomeLeftNavItems;
    const runtimeFilterParams = data?.runtimeFilterParams;
    const runtimeParameterParams = data?.runtimeParameterParams;
    const hostConfig = data?.hostConfig;
    const authToken = data?.authToken;
    const hiddenHomepageModules = data?.hiddenHomepageModules;
    const reorderedHomepageModules = data?.reorderedHomepageModules;
    const customVariablesForThirdPartyTools =
        data?.customVariablesForThirdPartyTools || {};
    const hiddenListColumns = data?.hiddenListColumns;
    setHiddenHomeLeftNavItems(hiddenHomeLeftNavItems);
    setCustomActions(data?.customActions || []);
    addCustomCssElements(data);
    if (runtimeFilterParams) {
        setRuntimeFilterParams(runtimeFilterParams);
    }
    if (runtimeParameterParams) {
        setRuntimeParameterParams(runtimeParameterParams);
    }
    setHostConfig(hostConfig);
    if (authToken) {
        setAuthToken(authToken);
    }
    setHiddenHomepageModules(hiddenHomepageModules);
    setReorderedHomepageModules(reorderedHomepageModules);
    setEmbedQueryParamsFromInitData(data);
    setCustomVariablesForThirdPartyToolsFromAppInit(
        customVariablesForThirdPartyTools,
    );
    setHiddenListColumns(hiddenListColumns);
    setInterceptUrls(data?.interceptUrls || []);
    setInterceptTimeout(data?.interceptTimeout);
};
